import React, { ReactNode } from 'react';

import type { LogErrorFn, SetContextFn } from '@strava/container-context';

import { MREContext } from '../../hooks/useMRE';
import { useMRELogError } from '../../hooks/useMRELogError';
import type { MREContextType } from '../../types';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';

interface ErrorBoundaryProps {
  children: ReactNode;
  message: string;
  logError: (
    error: Parameters<LogErrorFn>[0],
    context?: {
      name: Parameters<SetContextFn>[0];
      context: Parameters<SetContextFn>[1];
    },
    tags?: Record<string, string | number | boolean>
  ) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/*
 * Note: the ErrorBoundary will catch errors thrown in component lifecycle methods, but not
 * within event handlers. These still need to be handled with try/catch.
 */
export class ErrorBoundaryImpl extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static contextType = MREContext;
  context: MREContextType = {} as MREContextType;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ error, errorInfo });

    this.props.logError(
      error,
      {
        name: 'errorInfo',
        context: { componentStack: errorInfo.componentStack }
      },
      {
        'mre.isFromErrorBoundary': true
      }
    );
  }

  render() {
    const { hasError } = this.state;
    const { message } = this.props;

    if (hasError && message) {
      return <ErrorMessage message={message} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundaryImpl;

export const ErrorBoundary: React.FC<{
  children: ReactNode;
  message: string;
}> = ({ children, message }) => {
  const logError = useMRELogError();
  return (
    <ErrorBoundaryImpl message={message} logError={logError}>
      {children}
    </ErrorBoundaryImpl>
  );
};
